import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, EventEmitter, inject, Input,
  OnChanges, Output, SimpleChanges,
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {HttpErrorResponse} from '@angular/common/http';
import {FormsModule} from '@angular/forms';
import {TranslatePipe} from '@ngx-translate/core';
import {A11yModule} from '@angular/cdk/a11y';
import {NgStyle} from '@angular/common';
import {finalize, forkJoin, of} from 'rxjs';
import {CompanyPayload, CompanyService} from '../../../services/company.service';
import {AdvisorService} from '../../../services/advisor.service';
import {AdminService} from '../../../services/admin.service';
import {Company} from '../../../shared/model/company.model';
import {Advisor} from '../../../shared/model/advisor.model';
import {IUser} from '../../../shared/model/user.model';
import {MessageService} from '../../../shared/components/message.service';
import {loadAllPages} from '../../../shared/load-all-pages';

interface Row {
  _tempId: string;
  id?: string;
  abbreviation: string;
  name: string;
  color: string;
}

@Component({
  selector: 'app-companies-modal',
  templateUrl: './companies-modal.component.html',
  styleUrl: './companies-modal.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, TranslatePipe, A11yModule, NgStyle],
})
export class CompaniesModalComponent implements OnChanges {
  private readonly companyService = inject(CompanyService);
  private readonly advisorService = inject(AdvisorService);
  private readonly adminService = inject(AdminService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly messageService = inject(MessageService);
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() saved = new EventEmitter<Company[]>();

  rows: Row[] = [];
  userCountByCompany = new Map<string, number>();
  advisorCountByCompany = new Map<string, number>();
  pendingRemoval: {row: Row; targetCompanyId: string | null} | null = null;
  isLoading = false;
  isSaving = false;

  private originalSnapshot: Row[] = [];
  private tempCounter = 0;
  private advisors: Advisor[] = [];
  private users: IUser[] = [];
  private stagedAdvisorIds = new Set<string>();
  private stagedUserIds = new Set<string>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      this.reload();
    }
  }

  private reload(): void {
    this.isLoading = true;
    this.pendingRemoval = null;
    this.stagedAdvisorIds.clear();
    this.stagedUserIds.clear();
    // Alle Seiten: die Listen entscheiden hier, wer beim Loeschen einer Firma umgehaengt wird.
    forkJoin({
      companies: this.companyService.reload(),
      advisors: loadAllPages<Advisor>((page, size) => this.advisorService.getAdvisors(page, size), 200),
      users: loadAllPages<IUser>((page, size) => this.adminService.getUsersPage({}, page, size), 500),
    })
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({companies, advisors, users}) => {
          this.advisors = advisors;
          this.users = users;
          this.rows = [...companies].map(c => this.toRow(c));
          this.originalSnapshot = this.rows.map(r => this.cloneRow(r));
          this.rebuildUsage();
        },
        error: () => this.messageService.error('adminDash.companies.loadError'),
      });
  }

  private rebuildUsage(): void {
    const advisorCount = new Map<string, number>();
    for (const a of this.advisors) {
      if (!a.id) continue;
      for (const companyId of new Set(a.companyIds ?? [])) {
        advisorCount.set(companyId, (advisorCount.get(companyId) ?? 0) + 1);
      }
    }
    const userCount = new Map<string, number>();
    for (const u of this.users) {
      if (!u.companyId) continue;
      userCount.set(u.companyId, (userCount.get(u.companyId) ?? 0) + 1);
    }
    this.userCountByCompany = userCount;
    this.advisorCountByCompany = advisorCount;
  }

  private toRow(c: Company): Row {
    return {
      _tempId: c.id ?? `new-${++this.tempCounter}`,
      id: c.id,
      abbreviation: c.abbreviation ?? '',
      name: c.name ?? '',
      color: c.color || '#5F6368',
    };
  }

  private cloneRow(r: Row): Row {
    return {...r};
  }

  userCountOf(row: Row): number {
    return row.id ? (this.userCountByCompany.get(row.id) ?? 0) : 0;
  }

  advisorCountOf(row: Row): number {
    return row.id ? (this.advisorCountByCompany.get(row.id) ?? 0) : 0;
  }

  usageOf(row: Row): number {
    return this.userCountOf(row) + this.advisorCountOf(row);
  }

  onAbbrInput(row: Row, raw: string, input?: HTMLInputElement): void {
    // Ohne Whitespace, weil Badges und Filter die Firma über das Kürzel auflösen.
    row.abbreviation = (raw ?? '').replace(/\s+/g, '').toUpperCase().slice(0, 4);
    // Bleibt der Wert dabei gleich, schreibt ngModel nicht zurueck und das Feld zeigt weiter das Leerzeichen.
    if (input && input.value !== row.abbreviation) {
      input.value = row.abbreviation;
    }
  }

  badgeStyle(color: string): {[k: string]: string} {
    const hex = (color || '#5F6368').trim();
    const rgb = this.hexToRgb(hex);
    if (!rgb) {
      return {background: '#F5F5F7', color: '#5F6368', 'border-color': '#DADCE0'};
    }
    return {
      background: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.12)`,
      color: hex,
      'border-color': `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.35)`,
    };
  }

  hexDisplay(color: string): string {
    return (color || '').toUpperCase();
  }

  private hexToRgb(hex: string): {r: number; g: number; b: number} | null {
    const match = /^#([0-9a-fA-F]{6})$/.exec(hex);
    if (!match) return null;
    const int = parseInt(match[1], 16);
    return {r: (int >> 16) & 0xff, g: (int >> 8) & 0xff, b: int & 0xff};
  }

  addCompany(): void {
    this.rows = [
      ...this.rows,
      this.toRow({abbreviation: '', name: '', color: '#5F6368'} as Company),
    ];
  }

  removeCompany(row: Row): void {
    if (this.usageOf(row) > 0) {
      const first = this.rows.find(r => r._tempId !== row._tempId && !!r.id);
      this.pendingRemoval = {row, targetCompanyId: first?.id ?? null};
      return;
    }
    this.rows = this.rows.filter(r => r._tempId !== row._tempId);
  }

  get pendingUserCount(): number {
    return this.pendingRemoval ? this.userCountOf(this.pendingRemoval.row) : 0;
  }

  get pendingAdvisorCount(): number {
    return this.pendingRemoval ? this.advisorCountOf(this.pendingRemoval.row) : 0;
  }

  migrationTargets(): Row[] {
    if (!this.pendingRemoval) return [];
    return this.rows.filter(r => r._tempId !== this.pendingRemoval!.row._tempId && !!r.id);
  }

  cancelPendingRemoval(): void {
    this.pendingRemoval = null;
  }

  confirmPendingRemoval(): void {
    if (!this.pendingRemoval) return;
    const {row, targetCompanyId} = this.pendingRemoval;
    if (row.id) {
      this.applyLocalReassignment(row.id, targetCompanyId);
    }
    this.rows = this.rows.filter(r => r._tempId !== row._tempId);
    this.pendingRemoval = null;
  }

  /** Erst das Speichern schickt die Uebergabe; advisors/users tragen bis dahin den Zielzustand. */
  private applyLocalReassignment(fromCompanyId: string, toCompanyId: string | null): void {
    for (const a of this.advisors) {
      if (a.id && (a.companyIds ?? []).includes(fromCompanyId)) this.stagedAdvisorIds.add(a.id);
    }
    for (const u of this.users) {
      if (u.id && u.companyId === fromCompanyId) this.stagedUserIds.add(u.id);
    }
    this.advisors = this.advisors.map(a =>
      (a.companyIds ?? []).includes(fromCompanyId)
        ? {...a, companyIds: this.withoutCompany(a.companyIds, fromCompanyId, toCompanyId)}
        : a,
    );
    this.users = this.users.map(u =>
      u.companyId === fromCompanyId ? {...u, companyId: toCompanyId} : u,
    );
    this.rebuildUsage();
  }

  cancel(): void {
    this.rows = this.originalSnapshot.map(r => this.cloneRow(r));
    this.stagedAdvisorIds.clear();
    this.stagedUserIds.clear();
    this.pendingRemoval = null;
    this.close();
  }

  save(): void {
    if (this.isSaving) return;
    if (this.hasIncompleteRows) {
      this.messageService.error('adminDash.companies.incompleteError');
      return;
    }
    this.isSaving = true;

    const originalById = new Map(this.originalSnapshot.filter(r => r.id).map(r => [r.id!, r]));
    const currentById = new Map(this.rows.filter(r => r.id).map(r => [r.id!, r]));

    const toCreate = this.rows.filter(r => !r.id && this.isRowFilled(r));
    const toDelete = [...originalById.values()].filter(r => !currentById.has(r.id!));
    const toUpdate = [...currentById.values()].filter(r => {
      const orig = originalById.get(r.id!);
      return orig && this.hasChanged(orig, r) && this.isRowFilled(r);
    });

    // Vor den Loeschungen, sonst weist das Backend die Firma als belegt zurueck.
    const reassignCalls = [
      ...this.advisors
        .filter(a => !!a.id && this.stagedAdvisorIds.has(a.id))
        .map(a => this.advisorService.updateAdvisor(a)),
      ...this.users
        .filter(u => !!u.id && this.stagedUserIds.has(u.id))
        .map(u => this.adminService.reassignCompany(u, u.companyId ?? null)),
    ];

    const crudCalls = [
      ...toCreate.map(r => this.companyService.create(this.payloadOf(r))),
      ...toUpdate.map(r => this.companyService.update(r.id!, this.payloadOf(r))),
    ];

    const deleteCalls = toDelete.map(r => this.companyService.delete(r.id!));

    const phase1 = reassignCalls.length ? forkJoin(reassignCalls) : of([]);
    phase1
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          const phase2 = crudCalls.length ? forkJoin(crudCalls) : of([]);
          phase2
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => {
                const phase3 = deleteCalls.length ? forkJoin(deleteCalls) : of([]);
                phase3
                  .pipe(
                    finalize(() => {
                      this.isSaving = false;
                      this.cdr.markForCheck();
                    }),
                    takeUntilDestroyed(this.destroyRef),
                  )
                  .subscribe({
                    next: () => {
                      this.stagedAdvisorIds.clear();
                      this.stagedUserIds.clear();
                      this.saved.emit(this.companyService.companies());
                      this.close();
                    },
                    error: (err: unknown) => this.handleError(err, 'adminDash.companies.error.inUse'),
                  });
              },
              error: (err: unknown) => {
                this.isSaving = false;
                this.cdr.markForCheck();
                this.handleError(err, 'adminDash.companies.error.duplicate');
              },
            });
        },
        error: (err: unknown) => {
          this.isSaving = false;
          this.cdr.markForCheck();
          this.handleError(err, 'adminDash.companies.saveError');
        },
      });
  }

  close(): void {
    this.visible = false;
    this.pendingRemoval = null;
    this.visibleChange.emit(false);
  }

  onOverlayClick(): void {
    this.close();
  }

  /** Die geloeschte Firma raus, die Zielfirma rein — ohne Dublette, wenn der Betreuer sie schon fuehrt. */
  private withoutCompany(
    companyIds: string[] | undefined,
    fromCompanyId: string,
    toCompanyId: string | null,
  ): string[] {
    const remaining = (companyIds ?? []).filter(id => id !== fromCompanyId);
    if (!toCompanyId || remaining.includes(toCompanyId)) return remaining;
    return [...remaining, toCompanyId];
  }

  private payloadOf(r: Row): CompanyPayload {
    return {abbreviation: r.abbreviation.trim(), name: r.name.trim(), color: r.color};
  }

  isAbbrMissing(row: Row): boolean {
    return !this.isRowUntouched(row) && !row.abbreviation?.trim();
  }

  isNameMissing(row: Row): boolean {
    return !this.isRowUntouched(row) && !row.name?.trim();
  }

  isRowIncomplete(row: Row): boolean {
    return this.isAbbrMissing(row) || this.isNameMissing(row);
  }

  get hasIncompleteRows(): boolean {
    return this.rows.some(r => this.isRowIncomplete(r));
  }

  get incompleteRowLabels(): string {
    return this.rows
      .map((r, index) => ({r, index}))
      .filter(({r}) => this.isRowIncomplete(r))
      .map(({r, index}) => r.name.trim() || r.abbreviation.trim() || `#${index + 1}`)
      .join(', ');
  }

  /** Rohwerte statt trim(), damit eine Zeile mit reinen Leerzeichen gemeldet und nicht still verworfen wird. */
  private isRowUntouched(r: Row): boolean {
    return !r.id && !r.abbreviation && !r.name;
  }

  private isRowFilled(r: Row): boolean {
    return !!(r.abbreviation?.trim() && r.name?.trim());
  }

  private hasChanged(a: Row, b: Row): boolean {
    return a.abbreviation !== b.abbreviation
      || a.name !== b.name
      || a.color !== b.color;
  }

  /** conflictKey trennt die 409-Faelle: beim Anlegen ist das Kuerzel doppelt, beim Loeschen ist die Firma belegt. */
  private handleError(err: unknown, conflictKey: string): void {
    const status = err instanceof HttpErrorResponse ? err.status : 0;
    this.messageService.error(status === 409 ? conflictKey : 'adminDash.companies.saveError');
  }
}
